import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Info, Umbrella } from '@phosphor-icons/react';
import { fixMojibake } from '../../../../../../utils/text/fix-mojibake';
import { capitalizeWordsIfLowercase } from '../../../../../../utils/text/capitalize-words-if-lowercase';
import { coerceStringArrayField } from '../../../../../../utils/seo/coerce-string-array-field';
import { formatSrpSalaryDisplay } from '../../../../../../utils/data/format-job-card-labels';
import { getSkillIcon } from '../../../../../../utils/skills/skill-icons';
import { getBenefitIcon } from '../../../../../../components/filters/FilterBuildingBlocks';
import { buildSrpFacetHref } from '../../utils/buildSrpFacetHref';
import { getCurrencySymbol } from '../../utils/salaryConversion';
import { EditorialSalaryGauge } from './EditorialSalaryGauge';
import { getRecruitmentStageIcon } from './recruitment-stage-icons';
import { brandLinkHoverHandlers } from './brand-hover';
import { EditorialCompanyExtras } from './EditorialCompanyExtras';
import { CompanyCard, type CompanyCardProps } from '../CompanyCard';
import type { DisplayJob } from '../../../../../../types';
import type { FinnishNetSalaryEstimate } from '../../utils/estimateFinnishNetSalary';
import type { JobSalaryListingProvenance } from '../../../../../../utils/job-detail/resolve-job-salary-listing-provenance';
import { Msg, useFormatMessage } from '../../../../../../i18n-review/client/Msg';

export interface EditorialDossierProps {
  displayJob: DisplayJob;
  perks?: string[];
  salaryStats?: unknown;
  locationSalaryStats?: unknown;
  allSalaryStats?: unknown;
  jobSalaryMin?: number;
  jobSalaryMax?: number;
  currency?: string;
  salaryPeriod?: 'annual' | 'monthly' | 'daily' | 'hourly';
  jobLocale: string;
  jobLocation?: string[];
  jobTitle?: string;
  jobRoleLevel?: string;
  jobSalaryListingProvenance?: JobSalaryListingProvenance;
  displaySalaryLabel?: string | null;
  finnishNetSalaryEstimate: FinnishNetSalaryEstimate | null;
  finnishNetSalaryExplanationText: string;
  /** Deep link from the take-home figure to the tax iceberg calculator, prefilled with this job's gross salary. */
  taxIcebergHref?: string | null;
  /** Company's real brand colour (adjustedBrand.panel); used for the salary gauge and the plain-list/recruitment-timeline accent dots instead of the generic jc-accent green. */
  brandColor?: string;
  companyCardProps: CompanyCardProps;
}

const dossierKickerClass = 'text-[10.5px] font-extrabold uppercase tracking-wide text-[#8C8474] border-b border-[rgba(22,21,15,0.08)] dark:border-[#4A3520]/70 pb-1.5 mb-2.5';
const plainListClass = 'space-y-1.5';
/**
 * Shared skills/benefits/perks/vacation chip: plain white card + border + brand-tinted icon,
 * replacing the old fixed-color fills (green for skills, gold for benefits, tan for perks) that
 * always clashed with whichever colour the company's actual brandColor happened to be. The icon
 * carries the brand colour (falling back to a neutral muted tone when there isn't one); the chip
 * itself stays a plain surface so it reads calmly regardless of the company.
 */
const chipBaseClass = 'inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-lg border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] text-[#3D3529] dark:text-[#C4A882] text-[11px] font-semibold hover:bg-[#F8F5F0] dark:hover:bg-[#2A1D0F] transition-colors';
/**
 * Benefits/perks in the 160px desktop rail: one full-width row per item instead of a wrapped
 * chip cloud. Benefit labels are long ("Well-being / healthcare wallet"), so intrinsic-width
 * chips in a column that narrow wrapped inside themselves and fell into a ragged one-or-two-
 * per-row pattern; a stacked list keeps every row the same width and every label on one line.
 * Below lg the rail is a full-width block, where the chip cloud still reads fine.
 */
const benefitChipClass = `${chipBaseClass} lg:w-full`;
const benefitListClass = 'flex flex-wrap gap-1.5 lg:flex-col lg:flex-nowrap';

function PlainList({ items, brandColor }: { items: string[]; brandColor?: string }): React.ReactElement {
  return (
    <ul className={`${plainListClass} list-none p-0 m-0`}>
      {items.map((item, i) => (
        <li key={i} className="relative pl-3.5 text-[12.5px] leading-relaxed text-[#4A4438] dark:text-[#C4A882]">
          <span
            className={`absolute left-0 top-[9px] h-[5px] w-[5px] ${brandColor ? '' : 'bg-jc-accent'}`}
            style={brandColor ? { background: brandColor } : undefined}
            aria-hidden
          />
          {fixMojibake(item)}
        </li>
      ))}
    </ul>
  );
}

/**
 * TEC-512 editorial-rail redesign: the two-column "dossier"; a narrow left rail (Skills,
 * Education, Benefits, in that order per design decision #9, so Benefits lands roughly level
 * with the Company section on the right without merging the two) and a main column (salary
 * figure + gauge + take-home pay, Responsibilities, Requirements, Recruitment process, Company).
 * Reuses SalaryDistributionChart and CompanyCard exactly as the control layout does; only the
 * surrounding composition and the skill/benefit chip icons are new.
 */
export function EditorialDossier({
  displayJob,
  perks,
  salaryStats,
  locationSalaryStats,
  allSalaryStats,
  jobSalaryMin,
  jobSalaryMax,
  currency,
  salaryPeriod,
  jobLocale,
  jobLocation,
  jobTitle,
  jobRoleLevel,
  jobSalaryListingProvenance,
  displaySalaryLabel,
  finnishNetSalaryEstimate,
  finnishNetSalaryExplanationText,
  taxIcebergHref,
  brandColor,
  companyCardProps,
}: EditorialDossierProps): React.ReactElement {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const facetHref = (facetKey: string, value: string) => buildSrpFacetHref(intl.locale, facetKey, value);

  const benefits = coerceStringArrayField(displayJob?.benefits);
  const skillsAndTechnologies = coerceStringArrayField(displayJob?.skillsAndTechnologies);
  const responsibilities = coerceStringArrayField(displayJob?.responsibilities);
  const requirements = coerceStringArrayField(displayJob?.requirements);
  const recruitmentProcess = coerceStringArrayField(displayJob?.recruitmentProcess);
  const hasPerks = Boolean(perks && perks.length > 0);
  const hasVacationWeeks = Boolean(displayJob.vacationWeeks && displayJob.vacationWeeks > 0);
  const hasBenefits = benefits.length > 0 || hasPerks || hasVacationWeeks;
  const hasSkills = skillsAndTechnologies.length > 0;
  const hasEducation = Boolean(displayJob.educationLevel);
  const hasSalary = Boolean(salaryStats || locationSalaryStats || allSalaryStats);
  const isSalaryEstimate = jobSalaryListingProvenance !== 'employer_quote';

  // As-advertised pay before conversion to monthly EUR — only worth surfacing when the original
  // currency genuinely differs from EUR, since a same-currency period conversion (e.g. annual to
  // monthly) isn't the kind of thing a reader needs re-explained.
  const originalSalaryLabel =
    displayJob.originalCurrency &&
    displayJob.originalCurrency !== 'EUR' &&
    typeof displayJob.originalSalaryRangeMin === 'number' &&
    typeof displayJob.originalSalaryRangeMax === 'number'
      ? (() => {
          const symbol = getCurrencySymbol(displayJob.originalCurrency);
          const period = formatMessage({
            annual: { id: 'salaryStats.perYear', defaultMessage: 'per year', description: 'Salary period suffix' },
            monthly: { id: 'salaryStats.perMonth', defaultMessage: 'per month', description: 'Salary period suffix' },
            daily: { id: 'salaryStats.perDay', defaultMessage: 'per day', description: 'Salary period suffix' },
            hourly: { id: 'salaryStats.perHour', defaultMessage: 'per hour', description: 'Salary period suffix' },
          }[displayJob.originalSalaryPeriod ?? 'monthly']);
          return `${symbol}${Math.round(displayJob.originalSalaryRangeMin).toLocaleString(intl.locale)}–${Math.round(displayJob.originalSalaryRangeMax).toLocaleString(intl.locale)} ${period}`;
        })()
      : null;

  const [takeHomeInfoOpen, setTakeHomeInfoOpen] = useState(false);

  const netSalaryLabel = finnishNetSalaryEstimate
    ? formatSrpSalaryDisplay({
        salaryRangeMin: Math.round(finnishNetSalaryEstimate.netMin),
        salaryRangeMax: Math.round(finnishNetSalaryEstimate.netMax),
        // Not salaryIsEstimate: true here; this component's own "Est. take-home" label already
        // says it's an estimate; formatSrpSalaryDisplay would otherwise prefix its own "Estimated"
        // text too, stacking into "Est. take-home Estimated €X" (caught during local verification).
        salaryIsEstimate: false,
        locale: intl.locale,
      })
    : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-6">
      {/* Left rail: Skills, Education, Benefits (incl. perks); order per design decision #9. */}
      <div className="flex flex-col gap-4 lg:order-first order-last">
        {hasSkills ? (
          <div>
            <div className={dossierKickerClass}>
              <Msg id="jobDetailPage.skillsAndTechnologies" defaultMessage="Skills & Technologies" description="Section heading for skills and technologies list on job detail page" />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {skillsAndTechnologies.map((skill, index) => {
                const SkillIcon = getSkillIcon(skill);
                return (
                  <span key={`${skill}-${index}`} className={chipBaseClass}>
                    {SkillIcon ? <SkillIcon className="h-3.5 w-3.5 flex-none" style={{ color: brandColor ?? '#8C8474' }} /> : null}
                    {capitalizeWordsIfLowercase(fixMojibake(skill))}
                  </span>
                );
              })}
            </div>
          </div>
        ) : null}

        {hasEducation ? (
          <div>
            <div className={dossierKickerClass}>
              <Msg id="search.refinements.educationLevel.detail" defaultMessage="Education Level" description="Education level section heading on job detail page" />
            </div>
            <a
              href={facetHref('educationLevel', displayJob.educationLevel!)}
              className={`text-[12.5px] font-semibold text-[#4A4438] dark:text-[#C4A882] hover:underline underline-offset-2 ${brandColor ? '' : 'hover:text-jc-accent'}`}
              {...brandLinkHoverHandlers(brandColor)}
            >
              {fixMojibake(displayJob.educationLevel!)}
            </a>
          </div>
        ) : null}

        {hasBenefits ? (
          <div>
            <div className={dossierKickerClass}>
              <Msg id="jobDetailPage.benefitsAndPerks" defaultMessage="Benefits & Perks" description="Section heading for combined benefits and perks on job detail page" />
            </div>
            <div className={benefitListClass}>
              {benefits.map((benefit, index) => {
                const BenefitIcon = getBenefitIcon(benefit);
                return (
                  <a key={`${benefit}-${index}`} href={facetHref('benefits', benefit)} className={benefitChipClass}>
                    <span className="flex-none" style={{ color: brandColor ?? '#8C8474' }}>
                      <BenefitIcon className="h-3.5 w-3.5" />
                    </span>
                    {capitalizeWordsIfLowercase(fixMojibake(benefit))}
                  </a>
                );
              })}
              {/* Single presentation only; the control layout's JobDetailsSection.tsx renders
                  perks twice (once standalone, once again inside this same combined card); this
                  redesign fixes that by only ever rendering perks here, alongside benefits. */}
              {hasPerks
                ? perks!.map((perk, index) => {
                    const PerkIcon = getBenefitIcon(perk);
                    return (
                      <a key={`${perk}-${index}`} href={facetHref('perks', perk)} className={benefitChipClass}>
                        <span className="flex-none" style={{ color: brandColor ?? '#8C8474' }}>
                          <PerkIcon className="h-3.5 w-3.5" />
                        </span>
                        {capitalizeWordsIfLowercase(fixMojibake(perk))}
                      </a>
                    );
                  })
                : null}
              {hasVacationWeeks ? (
                <span className={benefitChipClass}>
                  <Umbrella className="h-3.5 w-3.5 flex-none" style={{ color: brandColor ?? '#8C8474' }} />
                  <Msg
                    id="jobDetailPageV2.vacationWeeks"
                    defaultMessage="{count, plural, one {# week vacation} other {# weeks vacation}}"
                    values={{ count: displayJob.vacationWeeks }}
                    description="Vacation weeks stat shown as a benefit-style chip, editorial-rail redesign"
                  />
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {/* Main column: salary, responsibilities, requirements, recruitment process, company. */}
      <div className="flex flex-col gap-5 min-w-0">
        {hasSalary ? (
          <div>
            <div className={dossierKickerClass}>
              <Msg id="jobDetailPageV2.salaryCompares" defaultMessage="How this salary compares" description="Heading for the salary benchmark section on the redesigned job detail page" />
            </div>
            {displaySalaryLabel || netSalaryLabel ? (
              <div className="flex flex-wrap items-baseline gap-2.5 mb-1">
                {displaySalaryLabel ? (
                  <span className="[font-family:'Space_Grotesk',sans-serif] font-bold text-[22px] text-[#16150F] dark:text-[#F3EFE4]">{displaySalaryLabel}</span>
                ) : null}
                {displayJob.salaryIncludesVariablePay ? (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#F0EDE5] dark:bg-[#2A1D0F] text-[#8C8474] text-[10px] font-bold uppercase tracking-wide">
                    <Msg id="jobDetailPageV2.includesVariablePay" defaultMessage="+ variable pay" description="Badge next to the salary figure when the ad states pay includes commission/bonus/OTE on top of base, editorial-rail redesign" />
                  </span>
                ) : null}
                {netSalaryLabel ? (
                  <span className="text-xs font-semibold text-[#4A4438] dark:text-[#C4A882] flex items-center gap-1">
                    <Msg
                      id="jobDetailPageV2.estTakeHome"
                      defaultMessage="Est. take-home {amount}"
                      values={{
                        amount: taxIcebergHref ? (
                          <a
                            href={taxIcebergHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`font-bold underline underline-offset-2 ${brandColor ? '' : 'decoration-jc-accent/40 hover:text-jc-accent'}`}
                            style={brandColor ? { textDecorationColor: `${brandColor}66` } : undefined}
                            {...brandLinkHoverHandlers(brandColor)}
                          >
                            {netSalaryLabel}
                          </a>
                        ) : (
                          <b>{netSalaryLabel}</b>
                        ),
                      }}
                      description="Estimated after-tax take-home pay, editorial-rail redesign, next to the gross salary figure"
                    />
                    <button
                      type="button"
                      onClick={() => setTakeHomeInfoOpen((v) => !v)}
                      aria-expanded={takeHomeInfoOpen}
                      aria-label={formatMessage({ id: 'jobDetailPageV2.estTakeHome.infoLabel', defaultMessage: 'What assumptions does this estimate use?', description: 'Accessible label for the info-toggle button next to the estimated take-home pay figure' })}
                      className="text-[#8C8474] cursor-pointer"
                    >
                      <Info className="h-3.5 w-3.5" weight="bold" />
                    </button>
                  </span>
                ) : null}
              </div>
            ) : null}
            {originalSalaryLabel ? (
              <p className="m-0 mb-1 text-[10.5px] font-semibold text-[#8C8474] dark:text-[#9C9384]">
                <Msg
                  id="jobDetailPageV2.originallyListedAs"
                  defaultMessage="Originally listed as {amount}"
                  values={{ amount: originalSalaryLabel }}
                  description="Note showing the as-advertised salary in its original currency/period before conversion to monthly EUR, editorial-rail redesign"
                />
              </p>
            ) : null}
            {isSalaryEstimate && displayJob.salaryEstimateSampleSize ? (
              <p className="m-0 mb-2 text-[10.5px] font-semibold text-[#8C8474] dark:text-[#9C9384]">
                <Msg
                  id="jobDetailPageV2.salaryEstimateSampleSize"
                  defaultMessage="Estimated from {count, plural, one {# comparable listing} other {# comparable listings}}"
                  values={{ count: displayJob.salaryEstimateSampleSize }}
                  description="Authority/confidence note next to an estimated (non-employer-quoted) salary figure, showing how many comparable listings the estimate is based on, editorial-rail redesign"
                />
              </p>
            ) : null}
            {/* Both stay mounted (guarded only by the underlying data existing), toggled via
                `hidden` instead of a ternary on takeHomeInfoOpen that swaps them in/out of the
                tree — a live text node here can get rewritten by Google Translate's <font>
                wrapper behind React's back, and mounting/unmounting at that DOM position is what
                triggers the removeChild/insertBefore crash (see install-translate-safe-dom-patches.ts). */}
            {finnishNetSalaryExplanationText ? (
              <p
                className={`m-0 mb-1 text-[10.5px] leading-relaxed text-[#8C8474] dark:text-[#9C9384] max-w-prose ${takeHomeInfoOpen ? '' : 'hidden'}`}
                aria-hidden={takeHomeInfoOpen ? undefined : true}
              >
                {finnishNetSalaryExplanationText}
              </p>
            ) : null}
            {taxIcebergHref ? (
              <a
                href={taxIcebergHref}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-block mb-3 text-[10.5px] font-bold hover:underline underline-offset-2 ${brandColor ? '' : 'text-jc-accent'} ${takeHomeInfoOpen ? '' : 'hidden'}`}
                style={brandColor ? { color: brandColor } : undefined}
                aria-hidden={takeHomeInfoOpen ? undefined : true}
              >
                <Msg
                  id="jobDetailPageV2.taxIcebergLink"
                  defaultMessage="See the full tax breakdown →"
                  description="Link from the estimated take-home pay figure to the tax iceberg calculator, prefilled with this job's salary, editorial-rail redesign"
                />
              </a>
            ) : null}
            <EditorialSalaryGauge
              salaryStats={salaryStats as never}
              locationSalaryStats={locationSalaryStats as never}
              allSalaryStats={allSalaryStats as never}
              jobSalaryMin={jobSalaryMin}
              jobSalaryMax={jobSalaryMax}
              salaryPeriod={salaryPeriod}
              jobLocation={jobLocation}
              jobTitle={jobTitle}
              jobRoleLevel={jobRoleLevel}
              jobSalaryListingProvenance={jobSalaryListingProvenance}
              brandColor={brandColor}
            />
          </div>
        ) : null}

        {responsibilities.length > 0 ? (
          <div>
            <div className={dossierKickerClass}>
              <Msg id="jobDetailPage.responsibilities" defaultMessage="Responsibilities" description="Section heading for job responsibilities list" />
            </div>
            <PlainList items={responsibilities} brandColor={brandColor} />
          </div>
        ) : null}

        {requirements.length > 0 ? (
          <div>
            <div className={dossierKickerClass}>
              <Msg id="jobDetailPage.requirements" defaultMessage="Requirements" description="Section heading for job requirements list" />
            </div>
            <PlainList items={requirements} brandColor={brandColor} />
          </div>
        ) : null}

        {recruitmentProcess.length > 0 ? (
          <div>
            <div className={dossierKickerClass}>
              <Msg id="jobDetailPage.recruitmentProcess" defaultMessage="Recruitment Process" description="Section heading for recruitment process timeline on job detail page" />
            </div>
            <ol className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-0 p-0 m-0 list-none relative">
              <span className="hidden sm:block absolute top-[9px] left-[4.5%] right-[4.5%] h-px bg-jc-border" aria-hidden />
              {recruitmentProcess.map((step, idx) => {
                const StageIcon = getRecruitmentStageIcon(step);
                return (
                  <li key={idx} className="flex sm:flex-1 sm:flex-col items-center sm:items-start gap-2 sm:gap-1.5 relative">
                    {StageIcon ? (
                      <span
                        className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full text-white relative z-[1]"
                        style={{ background: brandColor ?? 'var(--jc-accent, #3B5E45)' }}
                        aria-hidden
                      >
                        <StageIcon className="h-2.5 w-2.5" weight="bold" />
                      </span>
                    ) : (
                      <span
                        className={`h-[9px] w-[9px] flex-none rounded-full relative z-[1] ${brandColor ? '' : 'bg-jc-accent'}`}
                        style={brandColor ? { background: brandColor } : undefined}
                        aria-hidden
                      />
                    )}
                    <span className="text-[10.5px] font-bold text-[#4A4438] dark:text-[#C4A882]">{fixMojibake(step)}</span>
                  </li>
                );
              })}
            </ol>
          </div>
        ) : null}

        <CompanyCard {...companyCardProps} />

        <EditorialCompanyExtras
          employerOverview={displayJob.employerOverview}
          companyCoreValues={displayJob.companyCoreValues}
          companyAwardsAndCertifications={displayJob.companyAwardsAndCertifications}
          companyVideo={displayJob.companyVideo}
          officeImages={displayJob.officeImages}
          companyId={companyCardProps.companyId}
          jobLocale={jobLocale}
          linkedinUrl={displayJob.linkedinUrl}
          facebookUrl={displayJob.facebookUrl}
          instagramUrl={displayJob.instagramUrl}
          youtubeUrl={displayJob.youtubeUrl}
          tiktokUrl={displayJob.tiktokUrl}
          brandColor={brandColor}
        />
      </div>
    </div>
  );
}
